import React, { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";

import * as styles from "./generator-tour.module.scss";

interface IGeneratorTourProps {
    isOpen: boolean;
    onClose(): void;
}

interface ITourStep {
    target: string;
    title: string;
    description: string;
    highlightFirstChild?: boolean;
    highlightLastChild?: boolean;
    desktopPlacement?: "right";
    mobileTitle?: string;
    mobileDescription?: string;
}

interface ITargetRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

const steps: ITourStep[] = [
    {
        target: "filters",
        desktopPlacement: "right",
        title: "Dopasuj stylizację",
        description: "Tutaj zmienisz okazję, kolory, wzory, styl, porę roku i budżet.",
    },
    {
        target: "products",
        highlightFirstChild: true,
        desktopPlacement: "right",
        title: "Poznaj swoją stylizację",
        description: "Każda karta przedstawia jeden element zestawu. Otwórz produkt, aby sprawdzić jego szczegóły i dostępność.",
        mobileDescription: "Przeglądaj ubrania, przesuwając palcem w prawo lub w lewo. Możesz też kliknąć wybraną miniaturę na dole.",
    },
    {
        target: "lock",
        desktopPlacement: "right",
        title: "Zatrzymaj ulubiony element",
        description: "Kliknij kłódkę przy produkcie, którego nie chcesz zmieniać podczas kolejnego losowania.",
    },
    {
        target: "actions",
        title: "Wyczaruj kolejny zestaw",
        highlightFirstChild: true,
        description: "Przycisk „Wyczaruj” zmieni odblokowane elementy, zachowując te oznaczone kłódką.",
        mobileDescription: "Nową stylizację wygenerujesz, przesuwając palcem od dołu do góry na zdjęciu albo klikając „Wyczaruj”.",
    },
    {
        target: "actions",
        title: "Zapisz stylizację",
        highlightLastChild: true,
        description: "Kliknij „Zapisz styl”, aby zachować zestaw na swoim koncie i wrócić do niego później.",
        mobileTitle: "Zapisz stylizację",
        mobileDescription: "Kliknij „Zapisz styl”, aby zachować zestaw na swoim koncie i wrócić do niego później.",
    },
    {
        target: "help",
        title: "Samouczek jest zawsze pod ręką",
        description: "Kliknij znak zapytania, gdy zechcesz ponownie przejść przez wskazówki dotyczące generatora.",
    },
];

const GeneratorTour: React.FC<IGeneratorTourProps> = ({ isOpen, onClose }) => {
    const [stepIndex, setStepIndex] = useState(0);
    const [targetRect, setTargetRect] = useState<ITargetRect | null>(null);
    const step = steps[stepIndex];

    useEffect(() => {
        if (isOpen) setStepIndex(0);
    }, [isOpen]);

    useLayoutEffect(() => {
        if (!isOpen || typeof window === "undefined") return;

        const updateTarget = () => {
            const targets = Array.from(document.querySelectorAll<HTMLElement>(
                `[data-generator-tour="${step.target}"]`
            ));
            const target = targets.find(element => {
                const rect = element.getBoundingClientRect();
                const style = window.getComputedStyle(element);
                return rect.width > 0 && rect.height > 0
                    && style.display !== "none" && style.visibility !== "hidden";
            });
            if (!target) {
                setTargetRect(null);
                return;
            }

            const highlightedTarget = step.highlightFirstChild
                ? target.firstElementChild as HTMLElement | null
                : step.highlightLastChild
                    ? target.lastElementChild as HTMLElement | null
                    : target;
            if (!highlightedTarget) {
                setTargetRect(null);
                return;
            }

            const rect = highlightedTarget.getBoundingClientRect();
            const padding = 6;
            setTargetRect({
                top: Math.max(8, rect.top - padding),
                left: Math.max(8, rect.left - padding),
                width: Math.min(window.innerWidth - 16, rect.width + padding * 2),
                height: Math.min(window.innerHeight - 16, rect.height + padding * 2),
            });
        };

        updateTarget();
        window.addEventListener("resize", updateTarget);
        window.addEventListener("scroll", updateTarget, true);
        return () => {
            window.removeEventListener("resize", updateTarget);
            window.removeEventListener("scroll", updateTarget, true);
        };
    }, [isOpen, step]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKeydown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handleKeydown);
        return () => document.removeEventListener("keydown", handleKeydown);
    }, [isOpen, onClose]);

    if (!isOpen || typeof document === "undefined") return null;

    const isLastStep = stepIndex === steps.length - 1;
    const isMobile = window.innerWidth <= 570;
    const stepTitle = isMobile && step.mobileTitle ? step.mobileTitle : step.title;
    const stepDescription = isMobile && step.mobileDescription
        ? step.mobileDescription
        : step.description;
    const tooltipOnRight = step.desktopPlacement === "right" && window.innerWidth > 860;
    const tooltipOnTop = targetRect
        && !tooltipOnRight
        ? targetRect.top > window.innerHeight - targetRect.top - targetRect.height
        : false;

    return createPortal(
        <div className={styles.tour} role="dialog" aria-modal="true" aria-labelledby="generator-tour-title">
            <button
                type="button"
                className={styles.dismissLayer}
                onClick={onClose}
                aria-label="Zamknij samouczek"
            />
            {targetRect && (
                <div
                    className={styles.highlight}
                    style={{
                        top: targetRect.top,
                        left: targetRect.left,
                        width: targetRect.width,
                        height: targetRect.height,
                    }}
                />
            )}
            <section
                className={`${styles.tooltip} ${tooltipOnTop ? styles.tooltipTop : ""} ${
                    tooltipOnRight ? styles.tooltipRight : ""
                }`}
                style={targetRect ? {
                    left: tooltipOnRight
                        ? Math.min(targetRect.left + targetRect.width + 16, window.innerWidth - 402)
                        : Math.min(
                            Math.max(12, targetRect.left + targetRect.width / 2),
                            window.innerWidth - 12
                        ),
                    top: tooltipOnRight
                        ? Math.min(
                            Math.max(12 + 170, targetRect.top + targetRect.height / 2),
                            window.innerHeight - 12 - 170
                        )
                        : tooltipOnTop
                            ? Math.max(12, targetRect.top - 12)
                            : Math.min(window.innerHeight - 12, targetRect.top + targetRect.height + 12),
                } : undefined}
            >
                <div className={styles.progress} aria-label={`Krok ${stepIndex + 1} z ${steps.length}`}>
                    {steps.map((_, index) => (
                        <span key={index} className={index === stepIndex ? styles.progressActive : ""} />
                    ))}
                </div>
                <p className={styles.counter}>Krok {stepIndex + 1} z {steps.length}</p>
                <h2 id="generator-tour-title">{stepTitle}</h2>
                <p className={styles.description}>{stepDescription}</p>
                <div className={styles.actions}>
                    <button type="button" className={styles.skipButton} onClick={onClose}>
                        Pomiń
                    </button>
                    <div className={styles.navigation}>
                        {stepIndex > 0 && (
                            <button
                                type="button"
                                className={styles.backButton}
                                onClick={() => setStepIndex(index => index - 1)}
                            >
                                Wstecz
                            </button>
                        )}
                        <button
                            type="button"
                            className={styles.nextButton}
                            onClick={() => {
                                if (isLastStep) onClose();
                                else setStepIndex(index => index + 1);
                            }}
                        >
                            {isLastStep ? "Gotowe" : "Dalej"}
                        </button>
                    </div>
                </div>
            </section>
        </div>,
        document.body
    );
};

export default GeneratorTour;
